/* eslint-disable camelcase */
import { store } from '../redux/store';
import { ClassroomStudent, ClassroomStudents } from '../types/classroom';
import { NoTokenError, NoURLError } from './firebase';
import { Name } from '../types/user';
import { ROLES } from '../constants/roles';
export type JWT = string;

const getLtik = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const ltik = searchParams.get('ltik');
  if (!ltik) throw new NoTokenError('Missing lti key.');
  return ltik;
};

const getLtiUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const ltiUrl = searchParams.get('ltijsUrl');
  if (!ltiUrl) throw new NoURLError('Missing lti URL.');
  return ltiUrl;
};

export const isLTIResourceRequest = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const isLTIResourceRequest = searchParams.get('deeplinkResource');
  return isLTIResourceRequest;
};

interface LTIAuthToken {
  token: string;
}

/**
 * Send deeplink request to the ltijs server (which will route it to Canvas)
 * 
 * TODO: With dev-mode turned off, this response fails (login isn't triggered)
 * 
 * @param module
 *    Name of the skill to link an assignment or canvas module to
 * @returns
 *    Message from network
 */
export async function postDeepLink(
  module: string
): Promise<string | undefined> {
  const path = getLtiUrl() + '/deeplink';
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + getLtik(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: module,
        value: module,
      }),
    });

    if (response.ok && response.body) return readBody(response.body);
  } catch (e) {
    console.log('Deep link request failed: ', e);
  }
}

export async function getLTIAuthToken(): Promise<JWT> {
  const path = getLtiUrl() + '/token';
  const request: RequestInit = {
    method: 'GET',
    credentials: 'include',
    headers: { Authorization: 'Bearer ' + getLtik() },
  };
  const response = await fetch(path, request);
  const ltiAuthToken = (await response.json()) as LTIAuthToken;
  const jwt = ltiAuthToken.token;
  return jwt;
}

/**
 * Create a user id that we use internally in ITS and save in firebase
 * 
 * @param userId
 *    The user id taken directly from Canvas
 * @returns 
 */
export function createLTIUid(userId: string): string {
  const state = store.getState();
  const contextId = state.firebase.authUser?.claims?.contextIdEncoded;

  return `LTI:${userId}:${contextId}`;
}

interface LTIStudent {
  given_name: string;
  family_name: string;
  name: string;
  user_id: string;
  roles: string[];
}
type LTIRoster = LTIStudent[];

async function readBody(body: ReadableStream): Promise<string> {
  let result = '';
  if (body) {
    const stream = body.getReader();
    let chunk: any = '';
    do {
      chunk = await stream.read();
      if (chunk.value) result += String.fromCharCode(...chunk.value);
    } while (!chunk.done);
  }

  return result;
}

export function getRoleFromLTIRoles(roles: string[] = []): ROLES {
  const isStudent = !roles.every((v) => {
    return (
      v !== 'http://purl.imsglobal.org/vocab/lis/v2/membership#Learner' &&
      v !== 'Learner'
    );
  });
  const isTeacher =
    !isStudent &&
    !roles.every((v) => {
      return (
        v !== 'http://purl.imsglobal.org/vocab/lis/v2/membership#Instructor' &&
        v !== 'Instructor'
      );
    });

  const role = isStudent
    ? ROLES.STUDENT
    : isTeacher
    ? ROLES.TEACHER
    : ROLES.NONE;
  // console.log(`ROLE: ${role}`);
  return role;
}

/**
 * Send to LTI server (Canvas) the completion progress when an ITS skill/module was set as an
 * assignment. This is to speed up grading the assignment for the instructor when ITS is embedded in
 * something like canvas
 * 
 * @param completionProgress
 *    A value between 0 and 1 indicating percentage completion for this user for the current
 *    skill/module that has been set as an assignment.
 * @param manuallyGrade
 *    If true, do not have the grade value be finalized (i.e. teacher will still need to manually enter
 *    a grade in the grade book). Otherwise send the final grade.
 * @returns
 *    A boolean for whether the server successfully registered the completion request
 */
export async function sendCompletionProgress(
  completionProgress: number,
  manuallyGrade: boolean,
  ltiUrl: string,
  ltiToken: string
): Promise<boolean> {
  const request: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: 'Bearer ' + ltiToken,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      completionProgress,
      manuallyGrade
    })
  };
  try {
    const response = await fetch(`${ltiUrl}/grade`, request);
    const body = await response.json();
    console.log(body);
    return (response && body?.gradingProgress);
  } catch (err) {
    const result = await readBody((err as any).response.body);
    console.log(`send grade ERROR: ${result}`);
    return false;
  }
}

type ClassRoomContext = {
  name: string; // instructor name
}

export async function getClassRoomContext(): Promise<ClassRoomContext | undefined> {
  const infoUrl = `${getLtiUrl()}/info`;
  const request: RequestInit = {
    method: 'GET',
    credentials: 'include',
    headers: { Authorization: 'Bearer ' + getLtik() },
  };
  try {
    const response = await fetch(infoUrl, request);
    const results = (await response.json());
    console.log(JSON.stringify(results));
    return results as ClassRoomContext;
  } catch (err) {
    const result = await readBody((err as any).response.body);
    console.log(`getClassRoomContext ERROR: ${result}`);
    return undefined;
  }
}

export async function getRoster(): Promise<ClassroomStudents> {
  const path = getLtiUrl() + '/members';

  const classroomStudents: ClassroomStudents = {};
  const request: RequestInit = {
    method: 'GET',
    credentials: 'include',
    headers: { Authorization: 'Bearer ' + getLtik() },
  };

  let ltiRoster: LTIRoster | undefined = undefined;

  try {
    const response = await fetch(path, request);
    ltiRoster = (await response.json()) as LTIRoster;
  } catch (err) {
    const result = await readBody((err as any).response.body);
    console.log(`getRoster ERROR: ${result}`);
  }

  ltiRoster?.forEach((v) => {
    const roles = v.roles.map((role) => role.toLocaleLowerCase());
    const uid = createLTIUid(v.user_id);
    const given = v.given_name || '';
    const surname = v.family_name || (v.given_name === '' ? uid : '');
    const role = getRoleFromLTIRoles(roles);
    const name: Name = { given, surname };
    const username: string = v.name || uid;
    const student: ClassroomStudent = { uid, username, name, role, stats: {} };
    classroomStudents[uid] = student;
  });

  return classroomStudents;
}

export async function registerPlatform(
  accessKey: string,
  institution: string,
  canvasUrl: string,
  clientId: string
): Promise<boolean> {
  const request: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: 'Bearer ' + accessKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      canvasUrl,
      clientId,
      institution
    })
  };

  try {
    // No existing config that has this url, so just hard coding
    const ltiUrl = 'https://lti.its-bhss.org'; 
    const response = await fetch(`${ltiUrl}/registerPlatform`, request);
    return response.status === 200;
  } catch (error) {
    return false;
  }
}
